// © Thibault Sabathier, 2026. frontend/src/utils/httpErrors.js
//
// Traduction des statuts HTTP « structurels » en erreurs applicatives typées.
//
// ── POURQUOI UN MODULE À PART ──────────────────────────────────────────────
// Un 413 ne vient pas forcément d'Express : nginx coupe la requête sur
// `client_max_body_size` AVANT que l'application ne la voie, et répond une page
// HTML. Le corps n'est donc pas du JSON, aucun `code` applicatif n'y figure, et
// tout écran qui lit `error.response.data.code` retombe sur son message
// générique — c'est précisément ce qu'a vécu la publication multi-photos du
// 15/08 : « Une erreur est survenue lors de la publication », sans autre indice,
// et rien dans les logs applicatifs puisque la requête n'était jamais arrivée.
//
// Le mapping vit ici, hors des intercepteurs, pour deux raisons : il est
// partagé par `apiClient` et `recruiterApiClient` (deux systèmes d'auth
// distincts, même besoin), et il est testable sans DOM ni axios — le runner du
// dépôt tourne en `environment: 'node'`.
//
// ── INDÉPENDANT DES GARDES CÔTÉ CLIENT ─────────────────────────────────────
// Cette traduction ne remplace PAS le contrôle de budget de la sélection de
// photos : elle en est la défense de secours. Le client peut être contourné,
// un autre formulaire peut envoyer un fichier, l'overhead multipart peut
// varier, ou une autre limite serveur peut se déclencher. Le 413 doit rester
// compréhensible même quand rien ne l'avait anticipé.

/** Corps de requête refusé — par nginx (`client_max_body_size`) ou par multer. */
export const CODE_PAYLOAD_TOO_LARGE = 'PAYLOAD_TOO_LARGE'

/**
 * Construit l'erreur typée correspondant à une réponse HTTP, ou `null` si le
 * statut ne relève pas de ce module (l'appelant poursuit alors sa chaîne).
 *
 * @param {{status: number, data: any}} response
 * @returns {Error|null} Erreur portant `.status`, `.code` et `.response`.
 */
export const toTypedHttpError = (response) => {
  if (!response || response.status !== 413) return null

  // Le message du backend n'est repris que s'il est exploitable. Sur un 413
  // nginx, `data` est une page HTML complète : l'afficher à l'utilisateur
  // serait pire que le message générique.
  const backendMessage = typeof response.data?.message === 'string' ? response.data.message : null

  const error = new Error(backendMessage || 'Contenu trop volumineux')
  error.status = 413
  error.code = CODE_PAYLOAD_TOO_LARGE
  error.response = response
  return error
}

/** Un écran peut ainsi tester le code sans connaître le statut. */
export const isPayloadTooLarge = (error) => error?.code === CODE_PAYLOAD_TOO_LARGE
